import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useTRPC } from '@lib/trpc'

type Scope = { teamId: string; envId: string }

const EMPTY: Scope = { teamId: '', envId: '' }

export const useWorkosSummary = (scope: Scope | undefined, options: { poll?: boolean } = {}) => {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.workos.summary.queryOptions(scope ?? EMPTY),
    enabled: Boolean(scope),
    refetchInterval: options.poll ? 10_000 : false
  })
  return { workos: query.data ?? null, workosLoading: query.isPending }
}

export const useWorkosPreview = (scope: Scope | undefined, enabled: boolean) => {
  const trpc = useTRPC()
  const query = useQuery({ ...trpc.workos.preview.queryOptions(scope ?? EMPTY), enabled: Boolean(scope) && enabled, staleTime: 60_000, retry: false })
  return { preview: query.data ?? null, previewLoading: query.isPending && enabled, previewError: query.error }
}

export const useWorkosMoves = (scope: Scope | undefined, options: { poll?: boolean } = {}) => {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.workos.moves.queryOptions(scope ?? EMPTY),
    enabled: Boolean(scope),
    refetchInterval: options.poll ? 10_000 : false
  })
  return { moves: query.data ?? [], movesLoading: query.isPending }
}

export const useWorkosMove = (scope: Scope | undefined, tenant: string | undefined) => {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.workos.move.queryOptions({ ...(scope ?? EMPTY), tenant: tenant ?? '' }),
    enabled: Boolean(scope && tenant)
  })
  return { move: query.data ?? null, moveLoading: query.isPending }
}

const useInvalidateWorkos = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.workos.summary.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.workos.moves.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.workos.move.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.workos.preview.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.organizations.list.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.billing.status.queryKey() })
    ])
}

export const useConnectWorkosMutation = () => {
  const trpc = useTRPC()
  const invalidate = useInvalidateWorkos()
  const mutation = useMutation(trpc.workos.connect.mutationOptions({ onSuccess: invalidate }))
  return { connectWorkos: mutation.mutateAsync, connectWorkosIsPending: mutation.isPending }
}

export const useImportWorkosOrganizationMutation = () => {
  const trpc = useTRPC()
  const mutation = useMutation(trpc.workos.importOrganization.mutationOptions())
  return { importWorkosOrganization: mutation.mutateAsync }
}

export const useInvalidateWorkosMoves = useInvalidateWorkos

export const useSwitchWorkosMutation = () => {
  const trpc = useTRPC()
  const invalidate = useInvalidateWorkos()
  const mutation = useMutation(trpc.workos.switch.mutationOptions({ onSuccess: invalidate }))
  return { switchWorkos: mutation.mutateAsync, switchWorkosIsPending: mutation.isPending, switchingTenant: mutation.variables?.tenant }
}

export const useDisconnectWorkosMutation = () => {
  const trpc = useTRPC()
  const invalidate = useInvalidateWorkos()
  const mutation = useMutation(trpc.workos.disconnect.mutationOptions({ onSuccess: invalidate }))
  return { disconnectWorkos: mutation.mutateAsync, disconnectWorkosIsPending: mutation.isPending }
}
